/**
 * /welcome/:offerSku — where Spiffy sends a buyer the moment they pay for one
 * of the 16 price-card checkouts (plan docs/plans/post-purchase-journey-bkos-2026-09-30.md §4).
 *
 * The slug decides everything on the page (family, plan, cadence): the copy is
 * composed server-side by GET /api/public/welcome/:sku, so the page renders
 * correctly 4-8 seconds BEFORE the order webhook has written any membership
 * rows. A quiet status line flips once GET /api/public/welcome/status finds
 * them; if it never does, the buyer sees the receipt-email fallback and is
 * never blocked or shown an error.
 *
 * Identity: ?token= (from the welcome email, PR4) wins over Spiffy's appended
 * params (order, name_first, email, total), same as the program thanks page.
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { BRAND_NAME } from './branding';
import { useBrandGtm } from './lib/useBrandGtm';
import { useNoIndex } from './lib/useNoIndex';
import { MarketingOptIn } from './components/MarketingOptIn';
import BrokenPage from './components/BrokenPage';

const API = import.meta.env.VITE_API_BASE_URL as string;
const TSB_LOGO = 'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/brands/tsb/logo-light.svg';

interface Composed {
  sku: string;
  family: 'software' | 'bundle' | 'membership';
  brand: 'tsb-bkos' | 'tsb';
  cadence: 'monthly' | 'annual';
  cadenceLabel: string;
  productName: string;
  bkosPlan: { name: string } | null;
  membershipTier: { name: string } | null;
  headline: string;
  lede: string;
  firstWeek: FirstWeekStep[];
  memberPerk: { title: string; body: string } | null;
  setupCall: boolean;
  terms: { termMonths: number | null; moneyBackDays: number | null; cancelAnytime: boolean };
}
interface WelcomeEmail { title: string; when: string; from: string; body: string; tip: { lead: string; text: string } | null }
type FirstWeekId = 'workspace' | 'kickoff' | 'inbox' | 'clarity' | 'call';
interface FirstWeekStep { id: FirstWeekId; title: string; body: string; actionUrl: string | null; actionLabel: string | null; emails: WelcomeEmail[] }
interface Workspace { state: string; address: string | null }
type CountryCode = 'us' | 'ca' | 'au'
interface Status { ready: boolean; termEndsAt: string | null; moneyBackEndsAt: string | null; workspace: Workspace | null; suggestedCountry: CountryCode | null }

function longDate(raw: string | null): string {
  if (!raw) return '';
  const d = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z');
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function WelcomePage() {
  const { offerSku = '' } = useParams<{ offerSku: string }>();
  const [params] = useSearchParams();
  useBrandGtm();
  useNoIndex(true);

  const token = params.get('token');
  const total = params.get('total');
  const [tokenEmail, setTokenEmail] = useState<string | null>(null);
  // MyRoadmap's "Set up your BKOS workspace" link carries the order inside the
  // token rather than in the URL (orientation-actions.ts).
  const [tokenOrder, setTokenOrder] = useState<string | null>(null);
  const order = params.get('order') ?? params.get('order_id') ?? tokenOrder;
  const [page, setPage] = useState<Composed | null>(null);
  const [missing, setMissing] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  const [giveUp, setGiveUp] = useState(false);
  const [workspaceState, setWorkspaceState] = useState<string | null>(null);

  const email = tokenEmail ?? params.get('email') ?? params.get('em');
  const firstName = params.get('name_first') ?? params.get('fn');
  const lastName = params.get('name_last') ?? params.get('ln');

  useEffect(() => {
    let live = true;
    const qs = token ? `?token=${encodeURIComponent(token)}` : '';
    fetch(`${API}/api/public/welcome/${encodeURIComponent(offerSku)}${qs}`)
      .then(async (r) => {
        if (r.status === 404) { if (live) setMissing(true); return; }
        const data = await r.json() as { ok: boolean; page?: Composed; identity?: { email: string; order?: string | null } | null };
        if (!live || !data.ok || !data.page) return;
        setPage(data.page);
        if (data.identity?.email) setTokenEmail(data.identity.email);
        if (data.identity?.order) setTokenOrder(data.identity.order);
      })
      .catch(() => { /* the page just stays on its loading line; nothing to show */ });
    return () => { live = false; };
  }, [offerSku, token]);

  // A link to one step (#fw-workspace from MyRoadmap) lands on it. The page is
  // composed after load, so the browser's own jump to the anchor misses it.
  useEffect(() => {
    if (!page || !window.location.hash.startsWith('#fw-')) return;
    const id = window.location.hash.slice(1);
    const t = window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }), 50);
    return () => window.clearTimeout(t);
  }, [page]);

  useEffect(() => {
    document.title = `You're in · ${BRAND_NAME}`;
    return () => { document.title = BRAND_NAME; };
  }, []);

  // Poll for the membership rows: the redirect lands before order:complete does.
  useEffect(() => {
    if (!order || !email || status?.ready) return;
    let live = true;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const r = await fetch(`${API}/api/public/welcome/status?order=${encodeURIComponent(order)}&email=${encodeURIComponent(email)}`);
        const d = await r.json() as { ok: boolean; ready: boolean; termEndsAt: string | null; moneyBackEndsAt: string | null; workspace?: Workspace | null; suggestedCountry?: CountryCode | null };
        if (live && d.ok) { setStatus({ ready: d.ready, termEndsAt: d.termEndsAt, moneyBackEndsAt: d.moneyBackEndsAt, workspace: d.workspace ?? null, suggestedCountry: d.suggestedCountry ?? null }); if (d.ready) return; }
      } catch { /* retry */ }
      if (!live) return;
      if (tries >= 12) { setGiveUp(true); return; }
      timer = window.setTimeout(tick, 5000);
    };
    let timer = window.setTimeout(tick, 1500);
    return () => { live = false; window.clearTimeout(timer); };
  }, [order, email, status?.ready]);

  // The firm form is always on the page for a buyer we can identify, even when
  // the paid order has not landed (the status poll gives up after a minute).
  // It sends with the same retry, and "needed" is true until BKOS has details.
  const showForm = !!page && page.brand === 'tsb-bkos' && !!order && !!email && /^\S+@\S+\.\S+$/.test(email);
  const formWorkspace: Workspace = status?.workspace ?? { state: 'needed', address: null };

  // One stamp per order that the buyer was shown the form (mct_b6423299-4a2).
  const stampOrder = showForm && formWorkspace.state === 'needed' ? order : null;
  useEffect(() => {
    if (!stampOrder || !email) return;
    fetch(`${API}/api/public/welcome/form-shown`, {
      method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ order: stampOrder, email }),
    }).catch(() => { /* a stamp is never worth a visible error */ });
  }, [stampOrder, email]);

  if (missing) return <BrokenPage variant="404" />;
  if (!page) return <div style={wrap}><main style={main}><p style={lede}>One moment…</p></main></div>;

  // ?? []: a page bundle that lands before its worker (or a cached old
  // response) must still render, never crash on a missing field.
  const firstWeek = page.firstWeek ?? [];
  const wsState = workspaceState ?? status?.workspace?.state ?? null;
  const greeting = firstName ? `${firstName}, ${page.headline.charAt(0).toLowerCase()}${page.headline.slice(1)}` : page.headline;
  const bkos = page.brand === 'tsb-bkos';
  const emailOk = !!email && /^\S+@\S+\.\S+$/.test(email);
  // Spiffy's redirect sends `total` in cents (a $379 bundle arrives as
  // total=37900; seen on a live purchase 2026-10-08). A whole number is cents;
  // a value already written with a decimal point is taken as dollars.
  const paidDollars = total && /^[0-9]+$/.test(total) ? Number(total) / 100
    : total && /^[0-9]+\.[0-9]+$/.test(total) ? Number(total) : null;
  const paid = paidDollars !== null ? `$${paidDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;

  return (
    <div style={wrap}>
      <header style={nav}>
        <div style={navInner}>
          <a href="https://www.thesuccessfulbookkeeper.com" style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <img src={TSB_LOGO} alt="The Successful Bookkeeper" style={{ height: 32 }} />
            {bkos && <span style={bkosMark}>BKOS</span>}
          </a>
        </div>
      </header>
      <main style={main}>
        <p style={eyebrow}>Payment confirmed</p>
        <h1 style={h1}>{greeting}</h1>
        <p style={lede}>
          {page.lede}
          {paid && <> You paid <strong>{paid}</strong>.</>}
          {emailOk && <> It all goes to <strong>{email}</strong>.</>}
        </p>

        {firstWeek.length > 0 && (
          <section style={card}>
            <p style={cardHead}>Your first week starts here</p>
            <p style={{ margin: '0 0 8px', opacity: 0.75 }}>
              {['Two', 'Three', 'Four', 'Five'][firstWeek.length - 2] ?? firstWeek.length} steps, all on this page.
            </p>
            <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {firstWeek.map((f, i) => {
                const done = f.id === 'workspace' && !!wsState && wsState !== 'needed';
                const go = f.id === 'workspace' ? (done ? 'Done' : 'Start here ↓')
                  : f.id === 'kickoff' || f.id === 'clarity' ? 'Book now ↓'
                  : f.id === 'inbox' ? "See what's coming ↓"
                  : "In Lisa's email";
                const inner = (
                  <>
                    <span style={{ ...num, background: done ? OK : 'var(--prog-primary, #FF6B35)' }}>{done ? '✓' : i + 1}</span>
                    <span>
                      <span style={{ display: 'block', fontWeight: 600 }}>{f.title}</span>
                      <span style={{ ...goTag, color: done ? OK : f.id === 'call' ? 'rgba(10,10,46,0.55)' : 'var(--prog-primary, #FF6B35)' }}>{go}</span>
                    </span>
                  </>
                );
                return (
                  <li key={f.id} style={{ borderTop: i === 0 ? 'none' : '1px solid rgba(10,10,46,0.08)' }}>
                    {f.id === 'call'
                      ? <div style={stepRow}>{inner}</div>
                      : <a href={`#fw-${f.id}`} style={{ ...stepRow, color: 'inherit', textDecoration: 'none' }}
                          onClick={(e) => { e.preventDefault(); document.getElementById(`fw-${f.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>{inner}</a>}
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        {firstWeek.map((f, i) => (
          <section key={f.id} id={`fw-${f.id}`} style={{ ...card, borderLeft: '1px solid rgba(10,10,46,0.08)', scrollMarginTop: 16 }}>
            <p style={cardHead}>{i + 1} · {f.title}</p>
            <p style={{ margin: f.id === 'call' ? 0 : '0 0 16px' }}>{f.body}</p>
            {f.id === 'workspace' && (
              showForm && order && emailOk
                ? <BkosWorkspaceForm order={order} email={email as string} initial={formWorkspace} suggestedCountry={status?.suggestedCountry ?? null} onState={setWorkspaceState} />
                : <p style={{ margin: 0, opacity: 0.75 }}>If the form doesn't appear here, reply to Lisa's welcome email and we'll set up your workspace with you.</p>
            )}
            {(f.id === 'kickoff' || f.id === 'clarity') && (
              f.actionUrl
                ? <a href={f.actionUrl} style={cta} target="_blank" rel="noopener noreferrer">{f.actionLabel ?? 'Pick a time'} →</a>
                : <p style={{ margin: 0, opacity: 0.75 }}>{f.id === 'clarity' ? "Reply to Lisa's welcome email and we'll set it up with you." : "Lisa's welcome email has the link to book it."}</p>
            )}
            {f.id === 'inbox' && f.emails.map((m, j) => (
              <div key={m.title} style={{ display: 'grid', gridTemplateColumns: '36px 1fr', gap: 14, padding: '14px 0', borderTop: j === 0 ? 'none' : '1px solid rgba(10,10,46,0.08)' }}>
                <span style={mailIcon}>{j + 1}</span>
                <div>
                  <p style={{ margin: 0, fontWeight: 600 }}>{m.title} <span style={whenTag}>{m.when}</span></p>
                  <p style={{ margin: '2px 0 6px', fontSize: 13, opacity: 0.65 }}>{m.from}</p>
                  <p style={{ margin: 0 }}>{m.body}</p>
                  {m.tip && <p style={tipBox}><strong>{m.tip.lead}</strong> {m.tip.text}</p>}
                </div>
              </div>
            ))}
          </section>
        ))}

        {page.memberPerk && (
          <div style={card}>
            <p style={cardHead}>{page.memberPerk.title}</p>
            <p style={{ margin: 0 }}>{page.memberPerk.body}</p>
          </div>
        )}

        <p style={fine}>
          {status?.ready
            ? <>We've got your order and your plan is set up.
                {status.termEndsAt && <> Your 12-month commitment runs to <strong>{longDate(status.termEndsAt)}</strong>.</>}
                {status.moneyBackEndsAt && <> Your 30-day money-back window ends <strong>{longDate(status.moneyBackEndsAt)}</strong>.</>}</>
            : giveUp
              ? <>Your receipt email has everything you need, and our team has your order. If anything looks off, just reply to it.</>
              : order ? <>We're setting things up now…</> : null}
          {page.terms.cancelAnytime && !bkos && <> Cancel anytime.</>}
        </p>
        <p style={fine}>
          {order && <>Order reference: <code style={code}>{order}</code></>}
          {emailOk && <> · receipt sent to <code style={code}>{email}</code></>}
          {(order || emailOk) && '.'}
        </p>
        {emailOk && (
          <MarketingOptIn
            email={email}
            firstName={firstName}
            lastName={lastName}
            source="registration"
            sourceDetail={`welcome-${page.sku}`}
            brandLabel="The Successful Bookkeeper"
            style={{ marginTop: 20 }}
          />
        )}
      </main>
    </div>
  );
}

/** "Set up your BKOS workspace": the three details BKOS needs to create the
 *  firm's workspace (firm name, address, country). The Engine sends them on
 *  to BKOS (bkos-provision.ts); if BKOS cannot create it, the team finishes
 *  it by hand from the same details, so the buyer is never stuck. */
function suggestAddress(firm: string): string {
  return firm.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .slice(0, 40).replace(/-+$/g, '')
}

type Field = 'firmName' | 'workspaceAddress' | 'country'

/** The firm form. `transfer` is a BKOS transfer customer on /welcome/:sku/firm
 *  (TransferFirmPage): they have had no welcome email yet, so the done words
 *  point at our firm-details email instead (mct_b27e5971-bd1). */
export function BkosWorkspaceForm({ order, email, initial, onState, transfer = false, suggestedCountry = null }: { order: string; email: string; initial: Workspace; onState: (s: string) => void; transfer?: boolean; suggestedCountry?: CountryCode | null }) {
  const replyTo = transfer ? 'the email that brought you here' : "Lisa's welcome email"
  const [state, setStateRaw] = useState<string>(initial.state)
  const setState = (s: string) => { setStateRaw(s); onState(s) }
  const [address, setAddress] = useState<string | null>(initial.address)
  const [firmName, setFirmName] = useState('')
  const [workspaceAddress, setWorkspaceAddress] = useState('')
  const [addressTouched, setAddressTouched] = useState(false)
  const [country, setCountry] = useState<string>(suggestedCountry ?? '')
  const [countryTouched, setCountryTouched] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{ field: Field | null; message: string } | null>(null)

  // The form can open before the order lands; the status poll then brings the
  // billing-country suggestion (only until the buyer has chosen) or finds the
  // details already given.
  useEffect(() => { if (suggestedCountry && !countryTouched) setCountry(suggestedCountry) }, [suggestedCountry, countryTouched])
  useEffect(() => {
    if (initial.state !== 'needed') { setStateRaw(initial.state); setAddress(initial.address); onState(initial.state) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.state, initial.address])

  if (state === 'creating' || state === 'applied') {
    return (
      <div>
        <p style={doneLine}><span style={doneTick}>✓</span><span>Done. We're building {address ? <strong>{address}</strong> : 'your workspace'} now.</span></p>
        <p style={{ margin: '8px 0 0' }}>
          Two emails from BKOS come to <strong>{email}</strong>{transfer ? '' : ' (step 3)'}: one to set up your new workspace
          (click it to set your password, and you're in), and one to join the BKOS support community. They
          usually arrive within a few minutes. If they haven't come within the hour, just reply
          to {replyTo} and we'll sort it out.
        </p>
      </div>
    )
  }
  if (state !== 'needed') {
    return (
      <div>
        <p style={doneLine}><span style={doneTick}>✓</span><span>Done. We have your workspace details.</span></p>
        <p style={{ margin: '8px 0 0' }}>
          Our team is setting up your BKOS workspace and will email your login
          to <strong>{email}</strong> within one business day, usually much sooner.
        </p>
      </div>
    )
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const send = () => fetch(`${API}/api/public/welcome/bkos-details`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ order, email, firmName, workspaceAddress, country }),
    })
    try {
      let r = await send()
      // The order webhook can land a few seconds after the buyer does.
      for (let i = 0; r.status === 409 && i < 4; i++) {
        await new Promise((res) => setTimeout(res, 3000))
        r = await send()
      }
      const d = await r.json() as { ok: boolean; state?: string; address?: string | null; field?: Field; message?: string }
      if (d.ok && d.state) {
        setAddress(d.address ?? null)
        setState(d.state)
      } else {
        setError({ field: d.field ?? null, message: d.message ?? 'Something went wrong. Please try again.' })
      }
    } catch {
      setError({ field: null, message: 'We could not reach our servers. Please check your connection and try again.' })
    } finally {
      setBusy(false)
    }
  }

  const fieldErr = (f: Field) => error?.field === f ? <span style={errText}>{error.message}</span> : null

  return (
    <form onSubmit={submit}>
      <p style={{ margin: '0 0 18px' }}>
        Your login will be <strong>{email}</strong>.
      </p>
      <div style={{ display: 'grid', gap: 16 }}>
        <label style={label} htmlFor="bkos-firm-name">
          Firm name
          <input
            id="bkos-firm-name" style={input} value={firmName} required maxLength={120} autoComplete="organization"
            onChange={(e) => { setFirmName(e.target.value); if (!addressTouched) setWorkspaceAddress(suggestAddress(e.target.value)) }}
          />
          {fieldErr('firmName')}
        </label>
        <label style={label} htmlFor="bkos-address">
          Workspace address
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              id="bkos-address" style={{ ...input, flex: 1 }} value={workspaceAddress} required maxLength={63}
              autoCapitalize="none" spellCheck={false}
              onChange={(e) => { setAddressTouched(true); setWorkspaceAddress(e.target.value.toLowerCase()) }}
            />
            <span style={{ fontWeight: 500, opacity: 0.7 }}>.bkos.io</span>
          </span>
          <span style={hint}>Letters, numbers and hyphens. This is the web address your team will use.</span>
          {fieldErr('workspaceAddress')}
        </label>
        <label style={label} htmlFor="bkos-country">
          Where your firm is based
          <select id="bkos-country" style={input} value={country} required onChange={(e) => { setCountryTouched(true); setCountry(e.target.value) }}>
            <option value="" disabled>Choose a country</option>
            <option value="us">United States</option>
            <option value="ca">Canada</option>
            <option value="au">Australia</option>
          </select>
          <span style={hint}>This decides where your firm's data is stored.</span>
          {fieldErr('country')}
        </label>
      </div>
      {error && !error.field && <p style={{ ...errText, marginTop: 14 }}>{error.message}</p>}
      <button type="submit" style={{ ...cta, border: 0, cursor: busy ? 'wait' : 'pointer', marginTop: 20, opacity: busy ? 0.7 : 1 }} disabled={busy}>
        {busy ? 'Creating your workspace…' : 'Create my workspace'}
      </button>
    </form>
  )
}

const INK = '#0A0A2E';
const wrap: React.CSSProperties = { minHeight: '100vh', background: '#F8F6F3', color: INK, fontFamily: 'var(--font-body, "Poppins", -apple-system, sans-serif)' };
const nav: React.CSSProperties = { padding: '12px 24px', borderBottom: '1px solid rgba(10,10,46,0.08)', background: '#fff' };
const navInner: React.CSSProperties = { maxWidth: 760, margin: '0 auto', display: 'flex', alignItems: 'center' };
const bkosMark: React.CSSProperties = { fontWeight: 700, letterSpacing: 1.5, fontSize: 13, color: '#fff', background: INK, borderRadius: 6, padding: '4px 8px' };
const main: React.CSSProperties = { maxWidth: 760, margin: '0 auto', padding: '40px 24px 72px' };
const eyebrow: React.CSSProperties = { textTransform: 'uppercase', letterSpacing: 1.2, fontSize: 12, fontWeight: 600, opacity: 0.6, margin: '0 0 10px' };
const h1: React.CSSProperties = { fontFamily: 'var(--font-heading, "Fraunces", Georgia, serif)', fontSize: 'clamp(30px, 4.2vw, 40px)', fontWeight: 600, lineHeight: 1.15, margin: '0 0 16px' };
const lede: React.CSSProperties = { fontSize: 17, lineHeight: 1.6, opacity: 0.85, margin: '0 0 28px' };
const card: React.CSSProperties = { background: '#fff', borderRadius: 10, padding: '24px 28px', marginBottom: 20, borderLeft: '4px solid var(--prog-primary, #FF6B35)', lineHeight: 1.55 };
const cardHead: React.CSSProperties = { fontFamily: 'var(--font-heading, "Fraunces", Georgia, serif)', fontSize: 18, fontWeight: 600, margin: '0 0 12px' };
const cta: React.CSSProperties = { display: 'inline-block', background: 'var(--prog-primary, #FF6B35)', color: '#fff', textDecoration: 'none', fontWeight: 600, padding: '10px 18px', borderRadius: 8 };
const fine: React.CSSProperties = { fontSize: 13, opacity: 0.7, margin: '14px 0 0', lineHeight: 1.5 };
const OK = '#137A3B';
const num: React.CSSProperties = { width: 30, height: 30, borderRadius: '50%', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 };
const stepRow: React.CSSProperties = { display: 'grid', gridTemplateColumns: '34px 1fr', gap: 12, alignItems: 'center', padding: '12px 2px' };
const goTag: React.CSSProperties = { display: 'block', marginTop: 2, fontSize: 13, fontWeight: 600 };
const mailIcon: React.CSSProperties = { width: 36, height: 36, borderRadius: 10, background: '#FFF1EA', color: 'var(--prog-primary, #FF6B35)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 };
const whenTag: React.CSSProperties = { display: 'inline-block', fontSize: 12, fontWeight: 600, color: 'var(--prog-primary, #FF6B35)', background: '#FFF1EA', borderRadius: 999, padding: '2px 10px', marginLeft: 6, verticalAlign: 'middle' };
const tipBox: React.CSSProperties = { margin: '8px 0 0', fontSize: 14, background: '#F8F6F1', borderRadius: 8, padding: '8px 12px' };
const doneLine: React.CSSProperties = { margin: 0, display: 'flex', gap: 10, alignItems: 'center', color: OK, fontWeight: 600 };
const doneTick: React.CSSProperties = { flex: 'none', width: 22, height: 22, borderRadius: '50%', background: '#E7F6EC', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 };
const label: React.CSSProperties = { display: 'grid', gap: 6, fontWeight: 600, fontSize: 14 };
const input: React.CSSProperties = { font: 'inherit', fontWeight: 400, fontSize: 15, padding: '10px 12px', border: '1px solid rgba(10,10,46,0.2)', borderRadius: 8, background: '#fff', color: INK, minWidth: 0 };
const hint: React.CSSProperties = { fontWeight: 400, fontSize: 13, opacity: 0.65 };
const errText: React.CSSProperties = { fontWeight: 500, fontSize: 13, color: '#B42318' };
const code: React.CSSProperties = { fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 12, background: 'rgba(10,10,46,0.05)', padding: '2px 6px', borderRadius: 4 };

export default WelcomePage;
